import type { ColorToken } from './tokens';

/** A token, or `canvas` for the white board under the floating chrome. */
export type ContrastSide = ColorToken | 'canvas';

function luminance(hex: string): number {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? [...h].map((c) => c + c).join('') : h.slice(0, 6);
  const [r, g, b] = [0, 2, 4].map((i) => {
    const c = parseInt(full.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b!;
}

/** WCAG 2.x contrast ratio of two opaque colours (`#rgb` or `#rrggbb`), from 1 to 21. */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi! + 0.05) / (lo! + 0.05);
}

/** Text over a background: 4.5:1 (WCAG 1.4.3). */
export const TEXT_PAIRS: readonly [ContrastSide, ContrastSide][] = [
  ['fg', 'bg'],
  ['fg', 'surface'],
  ['fg', 'surface-2'],
  ['fg-muted', 'bg'],
  ['fg-muted', 'surface'],
  ['fg-muted', 'surface-2'],
  ['fg', 'chrome'],
  ['fg', 'chrome-hover'],
  ['fg-muted', 'chrome'],
  ['fg-muted', 'chrome-hover'],
  ['fg', 'accent-soft'],
  ['accent-fg', 'accent'],
  ['accent-fg', 'accent-hover'],
  // Text-only twins (P18): accent / fg-subtle as text sit on bg, chrome, surface and the accent tint.
  ['accent-text', 'bg'],
  ['accent-text', 'surface'],
  ['accent-text', 'chrome'],
  ['accent-text', 'chrome-hover'],
  ['accent-text', 'accent-soft'],
  ['fg-subtle-text', 'bg'],
  ['fg-subtle-text', 'surface'],
  ['fg-subtle-text', 'surface-2'],
  ['fg-subtle-text', 'chrome'],
  ['fg-subtle-text', 'chrome-hover'],
  ['danger', 'surface'],
  ['success', 'surface'],
  ['warning', 'surface'],
];

/** Component boundaries and focus indicators: 3:1 (WCAG 1.4.11). */
export const UI_PAIRS: readonly [ContrastSide, ContrastSide][] = [
  ['border-strong', 'surface'],
  ['border-strong', 'bg'],
  ['border-strong', 'chrome'],
  ['focus', 'bg'],
  ['focus', 'surface'],
  ['focus', 'canvas'],
  ['accent', 'surface'],
  ['fg-subtle', 'surface'],
];
